import { observer } from 'mobx-react-lite';
import { Button, Select } from 'antd';
import { CreditCardOutlined, DollarOutlined } from '@ant-design/icons';
import payeaseStore from '../../stores/payease.store';
import { formatPrice, passHasOptions } from '../../payease.utils';
import { PaymentMethod } from '../../payease.types';
import { Field } from '../ui/field/field';
import { TextField } from '../ui/text-field/text-field';
import { SectionLabel } from '../ui/section-label/section-label';
import './checkout-panel.scss';

export const CheckoutPanel = observer(() => {
  const { selectedPass, selectedOption, form } = payeaseStore;

  return (
    <div className="checkout-panel">
      <h2 className="checkout-panel__title">Checkout</h2>
      <p className="checkout-panel__subtitle">Confirm details and initiate payment.</p>

      <div className="checkout-panel__summary">
        {selectedPass ? (
          <div className="checkout-panel__summary-row">
            <div>
              <SectionLabel>Selected pass</SectionLabel>
              <p className="checkout-panel__summary-name">{selectedPass.name}</p>
              {selectedOption?.label && <p className="checkout-panel__summary-variant">{selectedOption.label}</p>}
              {passHasOptions(selectedPass) && !selectedOption && (
                <button type="button" className="checkout-panel__choose" onClick={() => payeaseStore.openOptionPicker(selectedPass.id)}>
                  Choose an option
                </button>
              )}
            </div>
            <p className="checkout-panel__summary-price">{formatPrice(payeaseStore.effectiveAmount)}</p>
          </div>
        ) : (
          <p className="checkout-panel__empty">No pass selected.</p>
        )}
      </div>

      <div className="checkout-panel__fields">
        <div className="checkout-panel__driver">
          <SectionLabel strong>Driver</SectionLabel>
          <div className="checkout-panel__driver-grid">
            <TextField
              id="payease-first-name"
              label="First Name"
              labelSize="sm"
              placeholder="Jane"
              value={form.firstName}
              onChange={(value) => payeaseStore.setFormField('firstName', value)}
            />
            <TextField
              id="payease-last-name"
              label="Last Name"
              labelSize="sm"
              placeholder="Doe"
              value={form.lastName}
              onChange={(value) => payeaseStore.setFormField('lastName', value)}
            />
          </div>
          <TextField
            id="payease-company"
            label="Company"
            labelSize="sm"
            placeholder="Acme Contracting"
            value={form.company}
            onChange={(value) => payeaseStore.setFormField('company', value)}
          />
          <TextField
            id="payease-email"
            label="Email (optional)"
            labelSize="sm"
            placeholder="jane@example.com"
            value={form.email}
            onChange={(value) => payeaseStore.setFormField('email', value)}
          />
        </div>

        <TextField
          id="payease-plate"
          label="License Plate"
          className="checkout-panel__plate"
          placeholder="ABC1234"
          spellCheck={false}
          transform={(value) => value.toUpperCase()}
          value={form.licensePlate}
          onChange={(value) => payeaseStore.setFormField('licensePlate', value)}
        />

        <Field label="State" htmlFor="payease-state">
          <Select
            id="payease-state"
            size="large"
            className="checkout-panel__state"
            placeholder="Select state"
            showSearch
            optionFilterProp="label"
            value={form.state || undefined}
            onChange={(value) => payeaseStore.setFormField('state', value)}
            options={payeaseStore.stateOptions}
          />
        </Field>
      </div>

      {payeaseStore.completedSale ? (
        <div className="checkout-panel__result">
          <p className="checkout-panel__result-title">
            {payeaseStore.completedSale.method === PaymentMethod.Cash ? 'Cash payment recorded' : 'Payment routed to the terminal'}
          </p>
          <p className="checkout-panel__result-text">
            {payeaseStore.completedSale.method === PaymentMethod.Cash
              ? 'Transaction completed.'
              : 'Complete the payment on the WisePOS E device.'}
          </p>
          <Button type="primary" block onClick={() => payeaseStore.startNewSale()}>
            New sale
          </Button>
        </div>
      ) : (
        <>
          <div className="checkout-panel__actions">
            <Button
              type="primary"
              className="checkout-panel__pay"
              block
              loading={payeaseStore.submittingMethod === PaymentMethod.Card}
              disabled={!payeaseStore.isReadyToSubmit}
              icon={<CreditCardOutlined />}
              onClick={() => payeaseStore.submit(PaymentMethod.Card)}>
              Initiate Payment
            </Button>
            <Button
              className="checkout-panel__cash"
              block
              loading={payeaseStore.submittingMethod === PaymentMethod.Cash}
              disabled={!payeaseStore.isReadyToSubmit}
              icon={<DollarOutlined />}
              onClick={() => payeaseStore.submit(PaymentMethod.Cash)}>
              Collect Cash
            </Button>
          </div>

          <p className="checkout-panel__footnote">Payment will be routed to the WisePOS E terminal.</p>
        </>
      )}
    </div>
  );
});
